import { Link } from "react-router-dom";
import type { Article } from "@/components/NewsSection";
import { getCategoryColor } from "@/lib/categoryColors";
import { getArticleUrl } from "@/lib/articleUrls";
import ArticleTagBadge from "@/components/ArticleTagBadge";
import MarketMoverBadge from "@/components/MarketMoverBadge";
import { getAuthor } from "@/data/journalists";
import { useVisitedArticles } from "@/hooks/useVisitedArticles";
import { useMarketMovers } from "@/hooks/useMarketMovers";
import { timeAgo } from "./timeAgo";
import { ListenButton } from "./TTSContext";
import SanityImage from "@/components/SanityImage";

export const StoryRow = ({ article, className }: { article: Article; className?: string }) => {
  const colors = getCategoryColor(article.category);
  const author = getAuthor(article.slug, article.author);
  const { isVisited } = useVisitedArticles();
  const visited = isVisited(article.slug);
  const { data: movers } = useMarketMovers();
  const mover = movers?.get(article.slug);
  return (
    <div className={`group flex gap-3 ${className || "py-2.5"}`}>
      <Link to={getArticleUrl(article)} className="w-20 h-14 rounded-md bg-secondary overflow-hidden shrink-0">
        <SanityImage
          src={article.image}
          alt={article.title}
          lqip={article.lqip}
          className="w-full h-full object-cover object-center opacity-80 group-hover:opacity-100"
          wrapperClassName="w-full h-full"
          loading="lazy"
          sizes="80px"
          width={960}
          height={540}
        />
      </Link>
      <div className="flex-1 min-w-0">
        <span className="text-[10px] font-semibold tracking-wider uppercase" style={{ color: colors.hex }}>
          {article.category}
        </span>
        {mover && <MarketMoverBadge delta={mover.delta_points} />}
        <Link to={getArticleUrl(article)}>
          <h3
            className={`text-[13px] font-bold leading-snug mt-0.5 line-clamp-2 hover-highlight transition-colors ${visited ? "text-muted-foreground" : "text-foreground"}`}
          >
            {article.title}
          </h3>
        </Link>
        <div className="flex items-center gap-1.5 mt-0.5">
          {author && (
            <p className="text-[11px] text-muted-foreground">
              {author.name} · {timeAgo(article.date)}
            </p>
          )}
          {article.readTime && parseInt(article.readTime) >= 4 && (
            <ListenButton id={article.slug} text={`${article.title}. ${article.excerpt}`} />
          )}
        </div>
      </div>
    </div>
  );
};

export const StoryCard = ({ article, showExcerpt = false }: { article: Article; showExcerpt?: boolean }) => {
  const colors = getCategoryColor(article.category);
  const author = getAuthor(article.slug, article.author);
  const { isVisited } = useVisitedArticles();
  const visited = isVisited(article.slug);
  const { data: movers } = useMarketMovers();
  const mover = movers?.get(article.slug);
  return (
    <div className="group">
      <Link to={getArticleUrl(article)} className="block">
        <div className="aspect-[16/9] rounded-lg bg-secondary overflow-hidden mb-2.5 relative">
          <SanityImage
            src={article.image}
            alt={article.title}
            lqip={article.lqip}
            className="w-full h-full object-cover object-center opacity-80 group-hover:opacity-100"
            wrapperClassName="w-full h-full"
            loading="lazy"
            width={960}
            height={540}
          />
          {article.tag && (
            <div className="absolute top-2 left-2">
              <ArticleTagBadge tag={article.tag} />
            </div>
          )}
        </div>
        <div className="flex items-center gap-1.5 mt-1">
          <span className="text-[10px] font-semibold tracking-wider uppercase" style={{ color: colors.hex }}>
            {article.category}
          </span>
          {mover && <MarketMoverBadge delta={mover.delta_points} />}
        </div>
        <h3
          className={`text-base font-bold leading-snug mt-1 line-clamp-2 hover-highlight transition-colors ${visited ? "text-muted-foreground" : "text-foreground"}`}
        >
          {article.title}
        </h3>
        {showExcerpt && <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{article.excerpt}</p>}
      </Link>
      <div className="flex items-center gap-1.5 mt-1.5">
        {author && (
          <p className="text-[11px] text-muted-foreground">
            {author.name} · {timeAgo(article.date)}
          </p>
        )}
        {article.readTime && parseInt(article.readTime) >= 4 && (
          <ListenButton id={article.slug} text={`${article.title}. ${article.excerpt}`} />
        )}
      </div>
    </div>
  );
};
